import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  signal,
  Signal,
  ViewChild,
} from '@angular/core';
import { Room } from '../../../core/models/entities/room';
import { take, takeUntil } from 'rxjs/operators';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import {
  BookingsDisplayMode,
  BookingsScope,
} from '../../../core/store/bookings/bookings.state';
import { BlockType, Booking } from '../../../core/models/entities/booking';
import { BookingDetailsModalComponent } from '../booking-details-modal/booking-details-modal.component';
import { ModalService } from '../../../core/services/modal.service';
import {
  addHours,
  addMinutes,
  addSeconds,
  differenceInMinutes,
  endOfDay,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  parse,
  parseISO,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { RoomDetailsModalComponent } from '../room-details-modal/room-details-modal.component';
import { BookableBy, BookableByHumanized } from '../../../core/models/common/bookable-by';
import { selectAllBookingsForRoom } from '../../../core/store/reducers';
import { isPlatformBrowser } from '@angular/common';
import { RoomICalSyncModalComponent } from '../../../shared/components/room-ical-sync-modal/room-ical-sync-modal.component';
import { PrivateBookingMainFormComponent } from '../private-booking-main-form/private-booking-main-form.component';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import { BookingCalendarNavigationService } from '../../../core/services/booking-calendar-navigation.service';

@Component({
  selector: 'app-booking-calendar-room',
  template: `
    <div
      class="flex h-full w-1/5 items-center justify-between gap-2 pr-1 pl-1 sm:pr-2 sm:pl-3"
      >
      <div
        class="line-clamp-2 text-sm font-medium"
        [tooltip]="room().full_name"
        [class.text-gray-muted]="!room().active"
        >{{ room().full_name }}</div
        >
    
        <div class="flex gap-1">
          <div tooltip="S'abonner au calendrier de cette salle">
            @if (room().active) {
              <app-icon-action
                icon="calendar-arrow-down"
                class="hidden lg:block"
                (click)="openICalSync(room())"
              ></app-icon-action>
            }
          </div>
    
          <div tooltip="Détails de la salle">
            @if (room().active) {
              <app-icon-action
                icon="eye"
                class="hidden lg:block"
                (click)="openRoomDetails(room())"
              ></app-icon-action>
            }
          </div>
    
          @if (!room().active) {
            <i
              class="fal fa-eye-slash text-gray-muted hidden sm:mx-4 lg:inline"
            ></i>
          }
        </div>
      </div>
    
      <div
        class="relative grow"
        [class.cursor-pointer]="canCreateBooking() && isHoverInFreeZone()"
        #calendarZone
        (mousemove)="onMouseMove($event)"
        (mouseleave)="onMouseLeave()"
        (click)="onCalendarClick($event)"
      >
        @for (block of dayOrWeekBlocks(); track '' + $index + block.left) {
          <div
          [style.left]="
            (displayMode?.() === BookingsDisplayMode.Week ? block.left : 0) +
            '%'
          "
          [style.width]="
            (displayMode?.() === BookingsDisplayMode.Week ? block.width : 100) +
            '%'
          "
            class="absolute top-0 left-0 flex h-full w-full border-l"
            >
          </div>
        }

        @for (block of dayOrWeekBlocks(); track '' + $index + block.left) {
          <div
            [style.left]="block.left + '%'"
            [style.width]="block.width + '%'"
            class="absolute top-0 left-0 flex h-full w-full border-l"
            [class.border-r]="$index === dayOrWeekBlocks()?.length - 1"
            >
          </div>
        }

        @for (block of roomUnavailableBlocks(); track block) {
          <div
            class="absolute top-0 h-full bg-gray-200"
            [style.left]="'calc(' + block.left + '% + 1px)'"
            [style.width]="'calc(' + block.width + '% - 1px)'"
          ></div>
        }

        @if (canCreateBooking() && hoverPosition() !== null && isHoverInFreeZone()) {
          <div
            class="pointer-events-none absolute top-1 bottom-1 rounded bg-gray-300/50 transition-opacity"
            [style.left]="hoverPosition() + '%'"
            [style.width]="hoverWidth() + '%'"
          ></div>
        }

        @for (booking of roomBookings(); track booking.id) {
          <div [class.hidden]="loadingBookings()">
            @if (isVisible(booking.start, booking.end)) {
              <app-booking-calendar-block
                class="absolute block"
                [style.width]="getBookingWidth(booking.start, booking.end)"
                [style.left]="getBookingLeftOffset(booking.start)"
                [room]="room()"
                [booking]="booking"
                [name]="booking.company ?? booking.name"
                [start]="booking.start"
                [end]="booking.end"
                [overflowsEnd]="overflowsEnd(booking.end)"
                [overflowsStart]="overflowsStart(booking.start)"
              [endsNextDay]="
                booking.duration.quantity === 1 && booking.ends_next_day
              "
                (click)="onBookingClick($event, booking)"
                />
            }
          </div>
        }
      </div>
    `,
  standalone: false,
})
export class BookingCalendarRoomComponent extends DestroyableComponent implements OnInit, AfterViewInit {
  room = input.required<Room>();
  scope = input.required<BookingsScope>();

  @ViewChild('calendarZone') calendarZone: ElementRef<HTMLDivElement>;

  roomBookings: Signal<Booking[]>;

  elWidth = signal(0);
  calendarWidth = computed(() => this.elWidth() * 0.8);
  displayMode: Signal<BookingsDisplayMode>;
  calendarDate: Signal<Date>;
  loadingBookings: Signal<boolean>;

  hoverPosition = signal<number | null>(null);
  private readonly HOVER_DURATION_HOURS = 1;

  hoverWidth = computed(() => {
    const hoursInScale = this.displayMode?.() === BookingsDisplayMode.Week ? 24 * 7 : 24;
    return (this.HOVER_DURATION_HOURS / hoursInScale) * 100;
  });

  canCreateBooking = computed(() => {
    const room = this.room();
    return room?.active && this.isRoomBookablePrivately(room);
  });

  isHoverInFreeZone = computed(() => {
    const pos = this.hoverPosition();
    if (pos === null) return false;

    const hoverStart = pos;
    const hoverEnd = pos + this.hoverWidth();

    if (this.collidesWithUnavailableBlocks(hoverStart, hoverEnd)) return false;
    if (this.collidesWithExistingBookings(hoverStart, hoverEnd)) return false;

    return true;
  });

  dayOrWeekBlocks: Signal<{ width: number; left: number }[]> = computed(() => {
    if (!this.displayMode || !this.calendarDate) return [];

    const numberOfBlocks =
      this.displayMode?.() === BookingsDisplayMode.Week
        ? 7
        : this.numberOfBlocksPerDay(this.calendarWidth());
    const date =
      this.displayMode?.() === BookingsDisplayMode.Week
        ? startOfWeek(this.calendarDate?.(), { weekStartsOn: 1 })
        : this.calendarDate?.();
    const blocks = [];
    for (let i = 0; i < numberOfBlocks; i++) {
      blocks.push({
        width:
          ((this.calendarWidth() / numberOfBlocks) * 100) /
          this.calendarWidth(),
        left:
          (((this.calendarWidth() / numberOfBlocks) * 100) /
            this.calendarWidth()) *
          i,
      });
    }
    return blocks;
  });

  roomUnavailableBlocks: Signal<{ width: number; left: number }[]>;
  platformId = inject(PLATFORM_ID);

  private bookingCalendarNavigationService = inject(BookingCalendarNavigationService);

  constructor(
    private store: Store,
    private modalService: ModalService,
    private elRef: ElementRef,
  ) {
    super();
  }

  ngOnInit() {
    this.roomBookings = this.store.selectSignal(
      selectAllBookingsForRoom(this.scope(), this.room().id),
    );
    this.calendarDate = this.store.selectSignal(
      fromStore.selectBookingsCalendarDate(this.scope()),
    );
    this.loadingBookings = this.store.selectSignal(
      fromStore.selectBookingsLoading(this.scope()),
    );
    this.displayMode = this.store.selectSignal(
      fromStore.selectBookingsDisplayMode(this.scope()),
    );

    this.roomUnavailableBlocks = computed(() => {
      if (
        this.displayMode?.() === BookingsDisplayMode.Week ||
        this.room().min_start === this.room().max_end
      ) {
        return [];
      }

      return isBefore(
        new Date(this.room().min_start),
        new Date(this.room().max_end),
      )
        ? [
            {
              left: 0,
              width: this.getOffsetFromStartOfDay(
                new Date(this.room().min_start),
              ),
            },
            {
              left: this.getOffsetFromStartOfDay(new Date(this.room().max_end)),
              width:
                100 -
                this.getOffsetFromStartOfDay(new Date(this.room().max_end)),
            },
          ]
        : [
            {
              left: this.getOffsetFromStartOfDay(new Date(this.room().max_end)),
              width:
                this.getOffsetFromStartOfDay(new Date(this.room().min_start)) -
                this.getOffsetFromStartOfDay(new Date(this.room().max_end)),
            },
          ];
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      const resizeObserver = new ResizeObserver((entries) => {
        this.elWidth.set(entries[0].contentRect.width);
      });

      resizeObserver.observe(this.elRef.nativeElement);
    }
  }

  numberOfBlocksPerDay(width: number) {
    const minWidth = 60;

    const hoursPerBlock = [24, 12, 8, 6, 4, 3, 2, 1];

    for (const hours of hoursPerBlock) {
      if (width / hours >= minWidth) {
        return hours;
      }
    }

    return 1;
  }

  openBooking(booking: Booking) {
    if (
      booking.block_type !== BlockType.Booking &&
      booking.block_type !== BlockType.Option
    ) {
      return;
    } else if (booking.block_type === BlockType.Option) {
      booking = this.roomBookings().find((loopBooking: Booking) => {
        return (
          loopBooking.date_group_id === booking.date_group_id &&
          loopBooking.block_type === BlockType.Booking
        );
      });
    }

    if (!booking) {
      return;
    }

    const instance = this.modalService.open(BookingDetailsModalComponent, {});
    const component = instance.componentRef
      .instance as BookingDetailsModalComponent;
    component.bookingId = booking.id;
  }

  openICalSync(room: Room) {
    const instance = this.modalService.open(RoomICalSyncModalComponent, {
      title: 'Abonnement calendrier',
      showActionButtons: false,
      size: 'lg',
    });
    const component = instance.componentRef
      .instance as RoomICalSyncModalComponent;
    component.setData(room);
  }

  openRoomDetails(room: Room) {
    this.store
      .select(fromStore.selectAuthVendor)
      .pipe(take(1))
      .subscribe((vendor) => {
        const instance = this.modalService.open(RoomDetailsModalComponent, {
          title: `
            <div class="flex items-center space-x-4">
              <span>Aperçu : ${room.full_name}</span>
              <div class="inline-block rounded-full font-bold bg-secondary-900 text-white px-5 py-1 text-sm">${
                BookableByHumanized[room.bookable_by]
              }</div>
            </div>`,
          size: 'screen',
          showActionButtons: false,
        });
        (instance.componentRef.instance as RoomDetailsModalComponent).room =
          room;
        (instance.componentRef.instance as RoomDetailsModalComponent).vendor =
          vendor;
      });
  }

  overflowsStart(start: string): boolean {
    const startDate = parseISO(start);
    const scaleStart =
      this.displayMode() === BookingsDisplayMode.Week
        ? startOfWeek(this.calendarDate(), { weekStartsOn: 1 })
        : startOfDay(this.calendarDate());

    return isBefore(startDate, scaleStart);
  }

  overflowsEnd(end: string): boolean {
    const endDate = parseISO(end);
    const scaleEnd = addSeconds(
      this.displayMode() === BookingsDisplayMode.Week
        ? endOfWeek(this.calendarDate(), { weekStartsOn: 1 })
        : endOfDay(this.calendarDate()),
      1,
    );
    return isAfter(endDate, scaleEnd);
  }

  isVisible(start: string, end: string): boolean {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const scaleStart =
      this.displayMode() === BookingsDisplayMode.Week
        ? startOfWeek(this.calendarDate(), { weekStartsOn: 1 })
        : startOfDay(this.calendarDate());
    const scaleEnd =
      this.displayMode() === BookingsDisplayMode.Week
        ? endOfWeek(this.calendarDate(), { weekStartsOn: 1 })
        : endOfDay(this.calendarDate());

    return isAfter(endDate, scaleStart) && isBefore(startDate, scaleEnd);
  }

  endTimeBeforeStart(start: string, end: string) {
    const startDate = parseISO(start);
    const endDate = parseISO(end);

    return isBefore(
      parse(format(endDate, 'HH:mm'), 'HH:mm', new Date()),
      parse(format(startDate, 'HH:mm'), 'HH:mm', new Date()),
    );
  }

  getBookingWidth(start: string, end: string): string {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const scaleStart =
      this.displayMode() === BookingsDisplayMode.Week
        ? startOfWeek(this.calendarDate(), { weekStartsOn: 1 })
        : startOfDay(this.calendarDate());
    const scaleEnd =
      this.displayMode() === BookingsDisplayMode.Week
        ? endOfWeek(this.calendarDate(), { weekStartsOn: 1 })
        : endOfDay(this.calendarDate());
    let bookingDurationOnScale = differenceInMinutes(endDate, startDate);
    const diffInMinutesFromScaleStart: number = differenceInMinutes(
      startDate,
      scaleStart,
    );
    if (diffInMinutesFromScaleStart < 0) {
      bookingDurationOnScale += diffInMinutesFromScaleStart;
    }
    const diffInMinutesToScaleEnd: number = differenceInMinutes(
      endDate,
      scaleEnd,
    );
    if (diffInMinutesToScaleEnd > 0) {
      bookingDurationOnScale -= diffInMinutesToScaleEnd;
    }

    if (this.displayMode() === BookingsDisplayMode.Week) {
      return `${(bookingDurationOnScale / 60 / (24 * 7)) * 100}%`;
    } else {
      return `${(bookingDurationOnScale / 60 / 24) * 100}%`;
    }
  }

  getBookingLeftOffset(start: string): string {
    const bookingStart = new Date(start);
    const scaleStart =
      this.displayMode() === BookingsDisplayMode.Day
        ? startOfDay(this.calendarDate())
        : startOfWeek(this.calendarDate(), { weekStartsOn: 1 });
    const diffFromStart = differenceInMinutes(bookingStart, scaleStart);

    let leftMargin = 0;
    if (this.displayMode() === BookingsDisplayMode.Week) {
      leftMargin = (diffFromStart / 60 / (24 * 7)) * 100;
    } else {
      leftMargin = (diffFromStart / 60 / 24) * 100;
    }

    return leftMargin > 0 ? `${leftMargin}%` : '0';
  }

  getOffsetFromStartOfDay(date: Date) {
    return (differenceInMinutes(date, startOfDay(date)) / (24 * 60)) * 100;
  }

  onMouseMove(event: MouseEvent) {
    if (!this.canCreateBooking() || !this.calendarZone) return;

    const rect = this.calendarZone.nativeElement.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    const percentageX = (relativeX / rect.width) * 100;

    // Centrer le hover sur la position de la souris
    const centeredPosition = percentageX - this.hoverWidth() / 2;

    // Limiter aux bornes [0, 100 - hoverWidth]
    const clampedPosition = Math.max(
      0,
      Math.min(centeredPosition, 100 - this.hoverWidth()),
    );

    this.hoverPosition.set(clampedPosition);
  }

  onMouseLeave() {
    this.hoverPosition.set(null);
  }

  onBookingClick(event: MouseEvent, booking: Booking) {
    event.stopPropagation();
    this.openBooking(booking);
  }

  onCalendarClick(event: MouseEvent) {
    if (!this.canCreateBooking() || !this.isHoverInFreeZone()) return;

    const clickDate = this.positionToDate(this.hoverPosition());
    if (!clickDate) return;

    this.openCreateBookingModal(clickDate);
  }

  private positionToDate(positionPercent: number): Date | null {
    if (positionPercent === null || !this.calendarDate?.()) return null;

    const scaleStart =
      this.displayMode?.() === BookingsDisplayMode.Week
        ? startOfWeek(this.calendarDate(), { weekStartsOn: 1 })
        : startOfDay(this.calendarDate());

    const totalMinutes =
      this.displayMode?.() === BookingsDisplayMode.Week ? 24 * 60 * 7 : 24 * 60;

    const minutesFromStart = (positionPercent / 100) * totalMinutes;

    // Arrondir aux 15 minutes les plus proches
    const roundedMinutes = Math.round(minutesFromStart / 15) * 15;

    return addMinutes(scaleStart, roundedMinutes);
  }

  private openCreateBookingModal(startDate: Date) {
    const endDate = addHours(startDate, this.HOVER_DURATION_HOURS);

    const instance = this.modalService.open(PrivateBookingMainFormComponent, {
      title: 'Nouvelle réservation',
      okAction: {
        title: 'Enregistrer',
      },
    });

    const component = instance.componentRef
      .instance as PrivateBookingMainFormComponent;

    component.initialData = {
      roomId: this.room().id,
      startDate,
      endDate,
    };

    outputToObservable(component.bookingSaved)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((startDate) =>
        this.bookingCalendarNavigationService.handlePrivateBookingSaved(startDate),
      );
  }

  private isRoomBookablePrivately(room: Room): boolean {
    return room?.bookable_by === BookableBy.Private || room?.bookable_by === BookableBy.Both;
  }

  private collidesWithUnavailableBlocks(hoverStart: number, hoverEnd: number): boolean {
    for (const block of this.roomUnavailableBlocks?.() ?? []) {
      if (hoverStart < block.left + block.width && hoverEnd > block.left) {
        return true;
      }
    }
    return false;
  }

  private collidesWithExistingBookings(hoverStart: number, hoverEnd: number): boolean {
    for (const booking of this.roomBookings?.() ?? []) {
      if (!this.isVisible(booking.start, booking.end)) continue;

      const bookingLeft = parseFloat(this.getBookingLeftOffset(booking.start));
      const bookingWidth = parseFloat(this.getBookingWidth(booking.start, booking.end));
      const bookingEnd = bookingLeft + bookingWidth;

      if (hoverStart < bookingEnd && hoverEnd > bookingLeft) {
        return true;
      }
    }
    return false;
  }

  protected readonly BlockType = BlockType;
  protected readonly BookingsDisplayMode = BookingsDisplayMode;
}
